import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ProductCard } from "./types";
import { productCards } from "./data";
import {
  animateHeading,
  animateCards,
  addCardHoverEffects,
  cleanupScrollTriggers,
} from "./animation";

const FeaturedProducts: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  // Track image load errors
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  useEffect(() => {
    // Initialize refs array with correct length
    cardsRef.current = new Array(productCards.length).fill(null);
  }, []);

  useEffect(() => {
    // Wait a small delay for DOM to be fully ready
    const animationTimeout = setTimeout(() => {
      // Apply heading animation
      animateHeading({ current: headingRef.current });

      // Apply card animations
      animateCards(
        { current: sectionRef.current },
        { current: cardsRef.current }
      );

      // Add hover effects to cards
      const cleanupHoverEffects = addCardHoverEffects({
        current: cardsRef.current,
      });

      // Return cleanup function
      return () => {
        cleanupHoverEffects();
        cleanupScrollTriggers();
      };
    }, 100);

    // Clean up timeout if component unmounts before timeout completes
    return () => clearTimeout(animationTimeout);
  }, []);

  // Check if file is a GIF
  const isGif = (path: string): boolean => {
    return path.toLowerCase().endsWith(".gif");
  };

  // Handle image error
  const handleImageError = (id: string) => {
    setImageErrors((prev) => ({ ...prev, [id]: true }));
  };

  // Get correct image path (ensure it starts with correct prefix)
  const getImagePath = (path: string): string => {
    // If path already starts with http, https, or /, return as is
    if (/^(https?:\/\/|\/)/i.test(path)) {
      return path;
    }
    // Otherwise, ensure it has a leading /
    return `/${path}`;
  };

  return (
    <section ref={sectionRef} className="py-16 bg-white text-gray-900">
      <div className="container mx-auto px-4">
        <h2
          ref={headingRef}
          className="text-3xl md:text-4xl font-bold text-center mb-12"
        >
          Featured Products
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {productCards.map((product: ProductCard, index: number) => (
            <div
              key={product.id}
              ref={(el) => (cardsRef.current[index] = el)}
              className="bg-gray-50 rounded-lg overflow-hidden shadow-md"
            >
              <div className="h-48 bg-gray-200 overflow-hidden relative">
                {product.image && !imageErrors[product.id] ? (
                  <>
                    {/* Regular image for GIFs */}
                    {isGif(product.image) ? (
                      <img
                        src={getImagePath(product.image)}
                        alt={product.title}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                        loading="eager"
                        onError={() => handleImageError(product.id)}
                      />
                    ) : (
                      /* Picture element with WebP support for non-GIFs */
                      <picture>
                        {product.imageWebp && (
                          <source
                            srcSet={getImagePath(product.imageWebp)}
                            type="image/webp"
                          />
                        )}
                        <source
                          srcSet={getImagePath(product.image)}
                          type={`image/${product.image.split(".").pop()}`}
                        />
                        <img
                          src={getImagePath(product.image)}
                          alt={product.title}
                          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                          loading="eager"
                          onError={() => handleImageError(product.id)}
                        />
                      </picture>
                    )}
                  </>
                ) : (
                  // Fallback for missing or failed images
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <div className="text-gray-400 text-center p-4">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-12 w-12 mx-auto mb-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <p>{product.title}</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">{product.title}</h3>
                <p className="text-gray-600 mb-4">{product.description}</p>
                <Link
                  to={product.link}
                  className="text-cannabis-green-500 font-medium hover:underline inline-flex items-center"
                >
                  {product.linkText}
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
